// Original AUDIO.CFG names and WAVs, with a bounded browser mixer. Priority,
// reverb and the original platform's spatial mixer remain separate parity work.
function tokens(line) {
  return (line.match(/"[^"\r\n]*"|[^\s]+/g) ?? []).map(token => token.startsWith('"') ? token.slice(1, -1) : token);
}
export function parseAudioConfig(text) {
  if (typeof text !== 'string' || text.length > 4 * 1024 * 1024) throw new Error('Invalid original audio configuration.');
  const samples = new Map(), groups = new Map(), duplicates = [];
  const flags = new Set(['global', 'loop', 'group', 'seq', 'disable', 'comment']);
  const numeric = new Set(['pitch', 'pri', 'pan', 'pitch_rnd', 'volume', 'volume_rnd', 'near', 'far', 'fcat']);
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.replace(/;.*$/, '').trim(); if (!line) continue;
    const [command, ...parts] = tokens(line);
    if (command.toLowerCase() === 'group') {
      if (!parts.length) throw new Error('Invalid audio group.');
      groups.set(parts[0].toLowerCase(), parts.map(name => name.toLowerCase())); continue;
    }
    if (command.toLowerCase() !== 'sample') continue;
    const entry = { flags: [], unsupported: [] };
    for (let i = 0; i < parts.length; i++) {
      const key = parts[i].toLowerCase();
      if (flags.has(key)) entry.flags.push(key);
      else if (key === 'name' || key === 'fname') { if (!parts[i + 1]) throw new Error('Missing audio name.'); entry[key] = parts[++i]; }
      else if (numeric.has(key)) {
        const value = Number(parts[++i]); if (!Number.isFinite(value)) throw new Error('Invalid audio numeric parameter.'); entry[key] = value;
      } else if (key === 'rumble') { entry.unsupported.push({ key, values: parts.slice(i + 1, i + 5) }); i += 4; }
      else entry.unsupported.push({ key });
    }
    if (entry.flags.includes('disable') || entry.flags.includes('comment')) { if (entry.name) samples.delete(entry.name.toLowerCase()); continue; }
    if (!entry.name || !entry.fname) throw new Error('Audio sample needs an original name and file.');
    entry.path = entry.fname.replaceAll('\\', '/').toLowerCase() + '.wav';
    if (entry.path.startsWith('/') || entry.path.split('/').some(part => !part || part === '.' || part === '..')) throw new Error('Invalid audio asset path.');
    // NuSound inserts each name at the head of its lookup chain, so the final
    // authored duplicate wins (the original table contains several).
    const key = entry.name.toLowerCase(); if (samples.has(key)) duplicates.push(key);
    samples.set(key, entry);
    if (samples.size > 20000) throw new Error('Too many audio samples.');
  }
  return { samples, groups, duplicates };
}
function waveRate(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength), tag = offset => String.fromCharCode(...bytes.subarray(offset, offset + 4));
  if (bytes.length < 12 || tag(0) !== 'RIFF' || tag(8) !== 'WAVE') throw new Error('Original sound is not RIFF/WAVE.');
  for (let offset = 12; offset + 8 <= bytes.length;) {
    const length = view.getUint32(offset + 4, true); if (offset + 8 + length > bytes.length) throw new Error('Truncated WAV chunk.');
    if (tag(offset) === 'fmt ' && length >= 16) { const rate = view.getUint32(offset + 12, true); if (!rate) throw new Error('Invalid WAV frequency.'); return rate; }
    offset += 8 + length + (length & 1);
  }
  throw new Error('Original WAV has no format chunk.');
}
export function createOriginalAudio(config, read, { onError = () => {}, random = Math.random } = {}) {
  const cache = new Map(), voices = new Map(), pending = new Map(), sequence = new Map();
  let context = null, master = null, cachedBytes = 0, generation = 0, serial = 0;
  const report = { state: 'idle', played: 0, missing: [], errors: [], cachedBytes: 0, mixerParity: false };
  async function resume() {
    if (!context) { context = new AudioContext(); master = context.createGain(); master.connect(context.destination); }
    await context.resume(); report.state = context.state;
  }
  function stop(id) { pending.delete(id); const voice = voices.get(id); if (!voice) return; voices.delete(id); voice.stop(); }
  async function play(name, { id = `sound:${serial++}`, gain = 1 } = {}) {
    if (!context || context.state !== 'running' || voices.has(id) || pending.has(id)) return null;
    const key = String(name).toLowerCase(), group = config.groups.get(key), leader = config.samples.get(key);
    let selected = key;
    if (group?.length) {
      const index = leader?.flags.includes('seq') ? (sequence.get(key) ?? 0) % group.length : Math.min(group.length - 1, Math.floor(random() * group.length));
      selected = group[index]; sequence.set(key, index + 1);
    }
    const sample = config.samples.get(selected);
    if (!sample) { if (!report.missing.includes(key)) report.missing.push(key); return null; }
    const currentGeneration = generation;
    const request = {}; pending.set(id, request);
    try {
      let decoded = cache.get(sample.path);
      if (!decoded) {
        const bytes = await read(sample.path), originalRate = waveRate(bytes);
        const buffer = await context.decodeAudioData(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
        decoded = { buffer, originalRate, size: buffer.length * buffer.numberOfChannels * 4 };
        while (cachedBytes + decoded.size > 32 * 1024 * 1024 && cache.size) { const first = cache.keys().next().value; cachedBytes -= cache.get(first).size; cache.delete(first); }
        if (decoded.size <= 32 * 1024 * 1024) { cache.set(sample.path, decoded); cachedBytes += decoded.size; report.cachedBytes = cachedBytes; }
      }
      if (generation !== currentGeneration || context.state !== 'running' || voices.has(id) || pending.get(id) !== request) return null;
      if (voices.size >= 32) stop(voices.keys().next().value);
      const source = context.createBufferSource(), volume = context.createGain();
      source.buffer = decoded.buffer; source.loop = sample.flags.includes('loop');
      source.playbackRate.value = (sample.pitch ?? decoded.originalRate) / decoded.originalRate;
      volume.gain.value = Math.max(0, Math.min(1, gain * (sample.volume === undefined ? 1 : sample.volume / 16383)));
      source.connect(volume); volume.connect(master); voices.set(id, source);
      source.onended = () => { if (voices.get(id) === source) voices.delete(id); source.disconnect(); volume.disconnect(); };
      source.start(); report.played++; return id;
    } catch (error) { report.errors.push({ name: selected, message: error.message }); onError(error); return null; }
    finally { if (pending.get(id) === request) pending.delete(id); }
  }
  function stopAll() { generation++; pending.clear(); for (const id of [...voices.keys()]) stop(id); }
  return { resume, play, stop, stopAll, report,
    setMuted(muted) { if (master) master.gain.value = muted ? 0 : 1; },
    dispose() { stopAll(); cache.clear(); cachedBytes = 0; context?.close().catch(onError); context = null; master = null; },
  };
}
